import React, { useState, useEffect, useRef } from 'react';
import { listingsAPI, paymentsAPI, addonsAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { X, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { isIosNativeApp, isAndroidNativeApp, loadAppleProductMap, purchaseApple } from '../utils/applePay';

// Server-side reconciliation poll — after the UPI app hands back control, the
// Razorpay `handler` callback is unreliable inside the Android WebView. We poll
// the backend (which confirms the payment directly with Razorpay and activates
// the benefit idempotently) so the add-on is applied even if `handler` drops.
const pollPaymentFulfillment = async (orderId, shouldStop, onFulfilled) => {
  const startedAt = Date.now();
  const MAX_MS = 3 * 60 * 1000;
  while (Date.now() - startedAt < MAX_MS) {
    await new Promise((r) => setTimeout(r, 4000));
    if (shouldStop()) return;
    try {
      const { data } = await paymentsAPI.status(orderId);
      if (data?.fulfilled) { onFulfilled(data); return; }
    } catch (_) { /* transient — keep polling */ }
  }
};

/**
 * Modal: lets the user pick which of their listings to apply a paid add-on to, then
 * runs the standard Razorpay flow → server-side signature verification → activate add-on.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - addon: { addon_id, name, price, kind }  (kind: 'pro' | 'boost')
 *  - preselectedListingId?: string  (when launched from Dashboard against a specific ad)
 *  - onSuccess?: (listing_id) => void
 */
const AddonPurchaseModal = ({ open, onClose, addon, preselectedListingId, onSuccess }) => {
  const [listings, setListings] = useState([]);
  const [selectedId, setSelectedId] = useState(preselectedListingId || '');
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [appleProductMap, setAppleProductMap] = useState({});
  const { user } = useAuth();
  // iter141 (Phase C) — Synchronous lock so a rapid double-tap on Android
  // WebViews can't fire handlePay twice before `paying` state propagates.
  const payingRef = useRef(false);
  // Guards against the handler AND the reconciliation poll both firing success.
  const finishedRef = useRef(false);

  useEffect(() => {
    if (isIosNativeApp) loadAppleProductMap().then(setAppleProductMap);
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedId(preselectedListingId || '');
    if (preselectedListingId) return; // no need to load list
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listingsAPI.getMy();
        if (!cancelled) {
          const eligible = (res.data || []).filter((l) => l.status === 'active');
          setListings(eligible);
          if (eligible.length === 1) setSelectedId(eligible[0].listing_id);
        }
      } catch (e) { toast.error('Failed to load your listings'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, preselectedListingId]);

  const handlePay = async () => {
    if (!user) { toast.error('Please log in first'); return; }
    if (!selectedId) { toast.error('Select a listing'); return; }
    if (!addon) return;
    if (payingRef.current) return;
    payingRef.current = true;

    // iOS native — App Store Guideline 3.1.1 requires Apple IAP for digital goods.
    if (isIosNativeApp) {
      setPaying(true);
      toast.info('Opening App Store…');
      try {
        let map = appleProductMap;
        if (!map || Object.keys(map).length === 0) {
          map = await loadAppleProductMap();
          setAppleProductMap(map);
        }
        const productId = map[addon.addon_id];
        if (!productId) {
          toast.error('This add-on is not available on iOS.');
          return;
        }
        await purchaseApple({ productId, listingId: selectedId });
        toast.success(`${addon.name} applied to your listing!`);
        onSuccess && onSuccess(selectedId);
        onClose();
      } catch (err) {
        toast.error(err?.response?.data?.detail || err?.message || 'Purchase failed. Please try again.');
      } finally {
        setPaying(false);
        payingRef.current = false;
      }
      return;
    }

    setPaying(true);
    try {
      finishedRef.current = false;
      const amount = addon.price * 100;
      const orderRes = await paymentsAPI.createOrder(amount, `Add-on: ${addon.name}`, {
        addon_id: addon.addon_id,
        listing_id: selectedId,
      });
      const applied = () => {
        if (finishedRef.current) return true;
        finishedRef.current = true;
        toast.success(`${addon.name} applied to your listing!`);
        onSuccess && onSuccess(selectedId);
        onClose();
        return false;
      };
      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: 'INR',
        name: 'GenzoX Marketplace',
        description: addon.name,
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            // Single-call atomic activation: verify captures the payment AND applies
            // the add-on server-side (addon context), so a dropped second request can
            // never leave a paid boost unapplied.
            await paymentsAPI.verify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              addon_id: addon.addon_id,
              listing_id: selectedId,
            });
            // Idempotent safety net (no-op if verify already applied it).
            await addonsAPI.applyToListing(selectedId, addon.addon_id, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
            });
            applied();
          } catch (err) {
            toast.error('Payment received but activation failed. Contact support with payment id ' + response.razorpay_payment_id);
          }
        },
        prefill: { name: user.name, email: user.email || '', contact: user.phone || '' },
        theme: { color: '#1800ad' },
        // iter140 — Android WebToNative shell: emit `upi://` intent links so
        // the native WebView can hand off to GPay/PhonePe/Paytm (Razorpay
        // UPI Intent flow). No effect on web or iOS.
        ...(isAndroidNativeApp ? { webview_intent: true } : {}),
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
      // Reconciliation net: applies the add-on via the backend even if the
      // Razorpay handler never fires (UPI Intent in the Android WebView).
      pollPaymentFulfillment(
        orderRes.data.order_id,
        () => finishedRef.current,
        () => { applied(); },
      );
    } catch (e) {
      // No silent "test mode" grant — a paid add-on must never be applied
      // without a completed, verified payment. Surface the error instead.
      toast.error(e?.response?.data?.detail || 'Payment could not be started. Please try again.');
    } finally { setPaying(false); payingRef.current = false; }
  };

  if (!open || !addon) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="addon-purchase-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="relative bg-gradient-to-br from-[#1800ad] to-[#ff8500] p-5 sm:p-6 rounded-t-2xl">
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white" data-testid="close-addon-modal">
            <X size={18} />
          </button>
          <h3 className="text-white text-lg sm:text-xl font-bold" data-testid="addon-modal-title">{addon.name}</h3>
          <p className="text-white/80 text-xs sm:text-sm mt-1">₹{addon.price} • Applied to a single listing</p>
        </div>

        <div className="p-4 sm:p-5">
          {preselectedListingId ? (
            <p className="text-sm text-[#4B5563] mb-4">This will be applied to the selected listing.</p>
          ) : (
            <>
              <p className="text-sm font-semibold text-[#0A0B10] mb-2">Choose which listing to apply this to:</p>
              {loading ? (
                <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#1800ad]" /></div>
              ) : listings.length === 0 ? (
                <p className="text-sm text-[#9CA3AF] py-6 text-center">You don't have any active listings yet. Post an ad first to apply add-ons.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1 mb-4">
                  {listings.map((l) => (
                    <button
                      key={l.listing_id}
                      data-testid={`pick-listing-${l.listing_id}`}
                      onClick={() => setSelectedId(l.listing_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors ${
                        selectedId === l.listing_id ? 'border-[#1800ad] bg-[#f0edff]' : 'border-gray-200 hover:border-[#1800ad]/40'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedId === l.listing_id ? 'border-[#1800ad] bg-[#1800ad]' : 'border-gray-300'}`}>
                        {selectedId === l.listing_id && <Check size={12} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#0A0B10] truncate">{l.title}</p>
                        <p className="text-xs text-[#6B7280]">
                          ₹{l.price?.toLocaleString()}
                          {l.is_pro && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#f0edff] text-[#1800ad] text-[10px] font-bold">PRO</span>}
                          {l.is_boosted && <span className="ml-2 px-1.5 py-0.5 rounded bg-[#fff4ed] text-[#ff8500] text-[10px] font-bold">BOOSTED</span>}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          <button
            data-testid="addon-modal-pay-btn"
            onClick={handlePay}
            disabled={paying || !selectedId || (!preselectedListingId && listings.length === 0)}
            className="w-full bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-full px-6 py-3 font-bold text-sm transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {paying ? <><Loader2 size={16} className="animate-spin" /> {isIosNativeApp ? 'Opening App Store…' : 'Opening payment…'}</> : `${isIosNativeApp ? 'Buy' : 'Pay'} ₹${addon.price} & Apply`}
          </button>
          <p className="mt-2 text-[11px] text-center text-[#9CA3AF]">{isIosNativeApp ? 'Secure purchase via App Store' : 'Secure payment via Razorpay'}</p>
        </div>
      </div>
    </div>
  );
};

export default AddonPurchaseModal;
